<template>
  <div class="constructor">
    <c-sidesheet title="Конструктор фильтров" :active="localActive" @close="close">
      <div class="constructor__wrapper">
        <div class="constructor__description">
          Перетаскивайте вправо названия полей, чтобы настроить их видимость в основном фильтре раздела. Поля в фильтре
          будут располагаться в том же порядке, что и в правом столбце конструктора.
        </div>

        <div class="constructor__content">
          <div class="constructor__search">
            <c-input-old
              v-model="searchInput"
              icon="search"
              placeholder-style="italic"
              placeholder="Поиск"
              :clearable="true"
            />
          </div>

          <div class="constructor__sections">
            <div class="constructor__section">
              <div class="constructor__section-title">Скрытые поля</div>

              <draggable
                v-model="disabled"
                class="constructor__section-content"
                :force-fallback="true"
                ghost-class="sortable__ghost"
                group="allFilters"
              >
                <div v-for="field in disabled" :key="field.name" class="constructor__element">
                  <div class="constructor__element-label">
                    <p>
                      <span>{{ field.label ? field.label : field.name }}</span> {{ field.description }}
                    </p>
                  </div>
                  <div class="constructor__element-icon">
                    <c-icon icon="positioning" />
                  </div>
                </div>
              </draggable>
            </div>

            <div class="constructor__section">
              <div class="constructor__section-title">Отображающиеся поля</div>

              <draggable
                v-model="enabled"
                class="constructor__section-content"
                :force-fallback="true"
                ghost-class="sortable__ghost"
                group="allFilters"
              >
                <div v-for="field in enabled" :key="field.name" class="constructor__element">
                  <div class="constructor__element-label">
                    <p>
                      <span>{{ field.label ? field.label : field.name }}</span> {{ field.description }}
                    </p>
                  </div>
                  <div class="constructor__element-icon">
                    <c-icon icon="positioning" />
                  </div>
                </div>
              </draggable>
            </div>
          </div>

          <div class="constructor__actions">
            <div class="constructor__actions-item">
              <c-button label="Отменить" @click="reset" />
            </div>

            <div class="constructor__actions-item">
              <c-button label="Сохранить" color="primary" @click="save" />
            </div>
          </div>
        </div>
      </div>
    </c-sidesheet>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import { mapGetters } from "vuex";

import Sidesheet from "@/components/sidesheet";
import Icon from "@/components/icon";

import InputOld from "@/components/controls2/Input";
import Button from "@/components/controls2/Button";

import { getFilterSettings } from "@/entities/structures/filter/index";

export default {
  name: "m-filter-constructor",

  components: {
    "c-sidesheet": Sidesheet,
    "c-input-old": InputOld,
    "c-icon": Icon,
    "c-button": Button,
    draggable,
  },

  props: {
    active: {
      type: Boolean,
      default: false,
    },
    module: {
      type: String,
      default: "",
    },
    currentFilters: {
      type: Array,
      default: () => [],
    },
    availableFilters: {
      type: Array,
      default: () => [],
    },
    enabledFilters: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      localActive: this.active,
      searchInput: "",
      disabled: [],
      enabled: [],
      enabledReserve: [],
      allFilters: [],
      userFiltersLocal: [],
    };
  },

  computed: {
    ...mapGetters("employees", ["getAllFilters", "getUserFilters", "saveUserFilters"]),
  },

  watch: {
    active(value) {
      this.localActive = value;
    },

    enabled(value) {
      this.enabledReserve = value;
    },

    searchInput(value) {
      const disabled = this.allFilters.filter((item) => {
        const enabledFields = this.enabled.map((i) => i.name);
        return !enabledFields.includes(item.name);
      });

      if (value !== "") {
        this.disabled = disabled.filter((item) => item.label.toLowerCase().includes(value.toLowerCase()));
      } else {
        this.disabled = disabled;
      }
    },

    getAllFilters(value) {
      if (this.allFilters.length === 0) {
        this.allFilters = value.map(getFilterSettings);
        this.disabled = [...this.allFilters];
      }
    },

    getUserFilters(value) {
      if (value !== undefined && value.filters !== undefined) {
        const enabledFilters = [];
        value.filters.forEach((filterName) => {
          const c = this.disabled.findIndex((filter) => filter.name === filterName);
          if (c !== -1) {
            enabledFilters.push(this.disabled[c]);
            this.disabled.splice(c, 1);
          }
        });
        if (enabledFilters.length > 0) {
          this.enabled = [...enabledFilters];
          this.userFiltersLocal = [...enabledFilters];
          this.$emit("applyFiltersFields", [...enabledFilters]);
        }
      }
    },

    currentFilters(value) {
      const enabledFilters = this.disabled.filter((item) => {
        const c = value.findIndex((filterName) => filterName === item.name);
        return c !== -1;
      });
      this.$emit("applyFiltersFields", [...enabledFilters]);
    },
  },

  async created() {
    if (this.module === "employees") {
      await this.$store.dispatch(`${this.module}/getAllFilters`);
      await this.$store.dispatch(`${this.module}/getUserFilters`);
      return;
    }

    if (this.availableFilters.length !== 0) {
      this.allFilters = this.availableFilters.map((filter) => ({ ...filter, label: filter.name, name: filter.code }));
    }

    this.enabledFilters.forEach((filter) => {
      const enabledFilter = this.allFilters.find((f) => f.code === filter);
      this.enabled.push(enabledFilter);
    });
    this.allFilters.forEach((filter) => {
      if (!this.enabledFilters.includes(filter.code)) {
        this.disabled.push(filter);
      }
    });
  },

  methods: {
    close() {
      this.$emit("close");
      setTimeout(() => {
        this.searchInput = "";
        const userFiltersNames = this.userFiltersLocal.map((item) => item.name);
        this.enabled = [...this.userFiltersLocal];
        this.disabled = this.allFilters.filter((filter) => !userFiltersNames.includes(filter.name));
      }, 200);
    },

    save() {
      if (this.module !== "employees") {
        this.$emit("save", [...this.enabled]);
        this.userFiltersLocal = [...this.enabled];
        this.$emit("close");
        return;
      }

      let enabledFields = [];
      let setUserFilters = [];

      enabledFields = [...this.enabled];

      setUserFilters = enabledFields.map((filter) => filter.name);

      this.$store.dispatch(`${this.module}/saveUserFilters`, setUserFilters).then(() => {
        this.userFiltersLocal = [...this.enabled];
      });

      this.$emit("save", enabledFields);

      this.$emit("close");
    },

    reset() {
      this.$emit("close");
      setTimeout(() => {
        this.searchInput = "";
        const userFiltersNames = this.userFiltersLocal.map((item) => item.name);
        this.enabled = [...this.userFiltersLocal];
        this.disabled = this.allFilters.filter((filter) => !userFiltersNames.includes(filter.name));
      }, 200);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/vars";

.constructor {
  &__wrapper {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  &__description {
    font: {
      size: 11px;
    }
    line-height: 12px;
    color: $color-label;
    margin-top: 16px;
    margin-bottom: 24px;
    flex-basis: 24px;
  }

  &__content {
    display: flex;
    flex-direction: column;
    height: calc(100% - 52px);
    position: relative;
  }

  &__search {
    width: 480px;
  }

  &__sections {
    margin-top: 27px;
    display: flex;
    flex-grow: 1;
    height: calc(100% - 120px);
  }

  &__section {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    position: relative;
    width: calc(50% - 24px);

    &:after {
      content: "";
      width: 1px;
      height: calc(100% - 36px);
      position: absolute;
      background: $color-border-light;
      right: 20px;
      top: 36px;
      z-index: 1;
    }

    &:before {
      content: "";
      width: 16px;
      height: calc(100% + 4px);
      position: absolute;
      background: white;
      right: 4px;
      top: 0;
      z-index: 1;
    }

    &-title {
      font: {
        size: 15px;
        weight: 500;
      }
      line-height: 18px;
    }

    &-content {
      margin-top: 18px;
      overflow-y: auto;
      padding-right: 19px;
      flex-grow: 1;
      position: relative;
      overflow-x: hidden;
      height: calc(100% - 36px);
      border: 1px solid $color-border-light;
      border-right: none;

      &::-webkit-scrollbar {
        width: 4px;
        height: 100%;
      }

      &::-webkit-scrollbar-thumb {
        background: $color-secondary;
        border-radius: 4px;
      }

      &::-webkit-scrollbar-track {
        background: $color-border-alt;
      }
    }

    &:first-child {
      margin-right: 24px;
    }

    &:last-child {
      margin-left: 24px;
    }
  }

  &__element {
    display: flex;
    justify-content: space-between;
    padding: 0 10px;
    align-items: center;
    margin: 0 -1px;
    width: calc(100% + 3px);
    height: 35px;
    position: relative;
    border-bottom: 1px solid $color-border-light;
    cursor: move;
    //touch-action: none;
    //transition: .2s;

    &-icon {
      width: 20px;
      height: 20px;
      flex-shrink: 0;
    }

    &-label {
      width: calc(100% - 30px);
      margin-right: 10px;
      font-size: 13px;

      p {
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
        color: $color-label;
        margin-bottom: 0;

        span {
          color: $color-font;
        }
      }
    }

    &.sortable-drag {
      height: 35px !important;
      z-index: 10 !important;
      opacity: 1 !important;
      background: white !important;
      border-bottom: none !important;
      box-shadow: 0px 3px 16px rgba(0, 102, 255, 0.7) !important;
    }

    &.sortable__ghost {
      height: 5px;
      overflow: hidden;
      background: $color-primary;
      opacity: 1;
      width: inherit !important;

      * {
        display: none;
      }
    }
  }

  &__actions {
    height: 38px;
    display: flex;
    margin-top: 30px;

    &-item {
      margin-right: 10px;
    }
  }
}

::v-deep .sortable-drag {
  opacity: 1 !important;
  z-index: 100;
}

::v-deep .sortable-drag .constructor__element {
  z-index: 10 !important;
}
</style>
